import clsx from "clsx"
import { ChangeEvent, FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Ton3dIcon } from "@/assets"
import { Button } from "@/ui/Button"
import styles from "./TonAmountInput.module.scss"
import { ITonAmountInput } from "./types"

const MAX_DECIMALS = 9

const formatForDisplay = (value: number | null): string =>
  value === null ? "" : String(value)

const sanitize = (raw: string): string => {
  let out = raw.replace(/[^0-9.]/g, "")

  const firstDot = out.indexOf(".")
  if (firstDot !== -1) {
    out =
      out.slice(0, firstDot + 1) + out.slice(firstDot + 1).replace(/\./g, "")
  }

  const [intPart, decPart] = out.split(".")
  if (decPart !== undefined && decPart.length > MAX_DECIMALS) {
    out = `${intPart}.${decPart.slice(0, MAX_DECIMALS)}`
  }

  return out
}

/**
 * `value` on the parent is `number | null` so "amount" stays a number
 * end-to-end. Internally we still hold a `displayValue` string because
 * mid-typing states like `"1."` are not representable as a number — the
 * parent is notified as `null` until the decimal is complete.
 */
export const TonAmountInput: FC<ITonAmountInput> = ({
  value,
  onChange,
  onSubmit,
  isLoading = false,
  isConnected,
  className,
  ...rest
}) => {
  const { t } = useTranslation()
  const [displayValue, setDisplayValue] = useState<string>(() =>
    formatForDisplay(value)
  )

  // Reflect external commits (e.g. preset chip click) into the local string.
  useEffect(() => {
    setDisplayValue(formatForDisplay(value))
  }, [value])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = sanitize(e.target.value)
    setDisplayValue(next)

    if (next === "" || next === ".") {
      onChange(null)
      return
    }

    // `"1."` is a valid mid-typing state — treat as null until the decimal
    // is complete. Otherwise Number(x) would round to `1` and round-trip to
    // the input, clobbering the user's in-progress typing.
    if (next.endsWith(".")) {
      onChange(null)
      return
    }

    const parsed = Number(next)
    onChange(Number.isFinite(parsed) ? parsed : null)
  }

  const buttonLabel =
    isConnected === false
      ? t("wallet.deposit.button.connect")
      : t("wallet.deposit.button.default")

  const submitDisabled = value === null || value <= 0 || isLoading

  return (
    <div {...rest} className={clsx(styles.TonAmountInput, className)}>
      <div className={styles.TonAmountInput_field}>
        <Ton3dIcon className={styles.TonAmountInput_icon} />
        <input
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleChange}
          placeholder="0"
          aria-label={t("wallet.deposit.amountLabel")}
        />
      </div>
      <Button styles="yellow" onClick={onSubmit} disabled={submitDisabled}>
        {buttonLabel}
      </Button>
    </div>
  )
}
